import React, { Fragment, useEffect, useState } from "react";
import { any, func, string } from "prop-types";
import { connect } from "react-redux";
import LogRocket from "logrocket";
import { Backdrop } from "../common";
import PulseBallLoader from "../common/PulseBallLoader";
import { clearErrors, getUserInProfile } from "../redux/actions";

/**
 * Higher order component to show loading screen while Profile data is loaded into store
 */

const TryAgain = ({ onClick }) => {
  return (
    <div className="w-100 d-flex flex-column justify-content-center align-items-center">
      <img src="/images/icons/timesheet-error.svg" />
      <h2 className="mt-2">Loading failed</h2>
      <b className="text-center">
        There was an error loading your information.
      </b>
      <button
        className="btn btn-primary font-weight-bold mt-2"
        onClick={onClick}
      >
        Try again
      </button>
    </div>
  );
};

TryAgain.propTypes = {
  onClick: func,
};

const LoadingError = ({ recordingId }) => {
  return (
    <div className="w-50 d-flex flex-column justify-content-center align-items-center text-center">
      <img src="/images/icons/timesheet-error.svg" />
      <h2 className="mt-2">Loading failed</h2>
      <b>
        There was an error loading your information. There appears to be an
        issue with your account, apologies for this inconvenience. Please
        contact our Customer Success team with the reference{" "}
        <mark className="text-nowrap">{recordingId}</mark>, Monday - Friday
        between 9am - 5.30pm on{" "}
        <a href="tel:01233515169" className="text-nowrap">
          01233 515169
        </a>
        , live chat or via email at
        <a href="mailto:customer.success@hrgo.co.uk" className="text-nowrap">
          {" "}
          customer.success@hrgo.co.uk
        </a>{" "}
        and they will be able to resolve the issue.
      </b>
      <a
        href="https://www.hrgo.co.uk"
        className="btn btn-primary font-weight-bold mt-2"
      >
        Back
      </a>
    </div>
  );
};

LoadingError.propTypes = {
  recordingId: string,
};

const LoadingScreen = () => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      // reload page if loading screen shown for 10 seconds
      document.location.reload();
    }, 60000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <Backdrop show={true}>
      <PulseBallLoader />
    </Backdrop>
  );
};

const withLoadingHOC = (Component) => {
  const WithLoading = (props) => {
    const { appData, errors, clearErrorsWithSource, getUser } = props;
    const userErrors = errors && errors.filter((err) => err.source === "user");

    const [clickedTwice, setClickedTwice] = useState(0);

    const handleTryAgain = () => {
      clearErrorsWithSource("user");
      getUser();
      setClickedTwice(clickedTwice + 1);
    };
    if (userErrors && userErrors.length !== 0) {
      const recordingID =
        LogRocket && LogRocket._logger && LogRocket._logger._recordingID;
      const { error } = userErrors[0];
      const quickResponse = error.duration && error.duration < 6000;
      if (recordingID && quickResponse && clickedTwice > 1) {
        return (
          <Backdrop show={true}>
            <LoadingError recordingId={recordingID} />
          </Backdrop>
        );
      }
      return (
        <Backdrop show={true}>
          <TryAgain onClick={handleTryAgain} />
        </Backdrop>
      );
    }
    if (!appData) {
      getUser();
      return <LoadingScreen />;
    }
    return <Component {...props} />;
  };
  WithLoading.displayName = `withLoading(${
    Component.displayName || Component.name
  }`;
  WithLoading.propTypes = {
    appData: any,
    errors: any,
    clearErrorsWithSource: func,
    getUser: func,
  };

  const mapStateToProps = (state) => ({
    appData: state.core.app,
    auth: state.core.auth,
    errors: state.core.errors,
  });

  const mapDispatchToProps = {
    clearErrorsWithSource: clearErrors,
    getUser: getUserInProfile,
  };

  return connect(mapStateToProps, mapDispatchToProps)(WithLoading);
};

export default withLoadingHOC;
